import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Badge, Button, Card, Field, Page, PageHeader, Select, Table } from '@evogard/ui';
import type { ContactBlock } from '@evogard/contract';
import { formatInstant, canAccess } from '@evogard/domain';
import { api, errorMessage } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';

// @valor
const LABEL_ORIGIN: Record<ContactBlock['origin'], string> = {
  pedido_do_titular: 'Pedido da pessoa',
  retorno_invalido: 'Retorno inválido',
  operacional: 'Operacional',
};

/**
 * Quem a casa não pode procurar.
 *
 * ## Esta lista não tem base
 *
 * É a única tela da interna cujo conteúdo **não muda** ao trocar de base no
 * seletor da moldura, e a tela diz isso com todas as letras. A regra 15 decidiu
 * que o opt-out vale para "toda base, todo canal, qualquer contato": bloquear
 * pela CE bloqueia na GO junto, e uma tela que não avisasse isso deixaria quem
 * administra achar que fez metade do trabalho.
 *
 * É também a diferença desta tela para todas as outras da plataforma, e por
 * isso o aviso é fixo e não um rodapé discreto.
 *
 * ## Manutenção, não atendimento
 *
 * Aqui se corrige a lista: bloquear alguém que ligou pedindo, desbloquear o
 * e-mail que voltou a funcionar. O opt-out do atendimento é gravado pela
 * cobrança, na onda 5, pelo mesmo service de `common/` — não por esta tela.
 *
 * ## Desbloquear é arquivar
 *
 * A operação nunca apaga (convenção 9). Apagar tiraria da trilha a prova de que
 * a pessoa pediu, que é o único registro de um direito exercido.
 */
export function ContactBlockScreen() {
  const { data: context } = useSessionContext();
  const client = useQueryClient();
  const [includeArchived, setIncludeArchived] = useState(false);

  const canEdit = canAccess(
    context?.permissions ?? [],
    'platform.contact_blocks',
    'editar',
  );

  const contactBlockQuery = useQuery({
    /*
     * SEM a base na chave — e é o contrário de toda outra lista desta casa.
     *
     * Nas outras, esquecer o tenant na chave mostra a linha de uma praça sob o
     * cabeçalho de outra. Aqui a lista é a mesma nas duas, e pôr o tenant seria
     * guardar duas cópias idênticas no cache com nomes diferentes — e afirmar,
     * para quem ler a chave, que a lista muda com a base.
     *
     * A busca acontece de novo na troca de qualquer jeito: `lib/session.ts` faz
     * `invalidateQueries()` sem chave ao trocar de praça, o que alcança tudo.
     */
    queryKey: ['plataforma', 'bloqueio-de-contato', includeArchived],
    queryFn: async () => {
      const r = await api.platform.listBlocks({
        query: includeArchived ? { includeArchived: 'true' } : {},
      });
      if (r.status !== 200) {
        throw new Error(errorMessage(r.body, 'Não foi possível carregar a lista.'));
      }
      return r.body.items;
    },
  });

  const invalidate = () =>
    client.invalidateQueries({ queryKey: ['plataforma', 'bloqueio-de-contato'] });

  return (
    <Page measure="cheia">
      <PageHeader title="Bloqueio de contato">
        Quem está aqui não recebe e-mail da Evogard.
      </PageHeader>

      <Alert tone="alerta" title="Esta lista não é da base atual.">
        Vale para <strong>todas as bases</strong>. Bloquear um contato pela{' '}
        {context?.currentTenant.nome ?? 'base atual'} bloqueia nas outras junto — e trocar
        de base no menu não muda o que aparece aqui.
      </Alert>

      {/*
       * O que a lista corta HOJE, escrito porque a ausência não se explica
       * sozinha.
       *
       * A decisão do negócio é "toda base, todo canal". A base já vale; o canal
       * ainda não, porque a casa só tem um remetente — o e-mail. Dizer "todos os
       * canais" seria a tela afirmando o que o código não cumpre, e quem
       * bloqueasse um telefone acharia que a operação parou de ligar.
       */}
      <Alert tone="informacao" title="O que este bloqueio corta hoje">
        Só o <strong>e-mail</strong>, que é o único canal que a casa envia por conta
        própria. Telefone e WhatsApp entram nas ondas 4 e 5, com a régua de cobrança e a
        esteira de retenção — até lá, bloquear um telefone aqui registra o pedido e{' '}
        <strong>não impede ninguém de ligar</strong>. Registre mesmo assim: é o que faz a
        régua já nascer respeitando quem pediu.
      </Alert>

      {canEdit && <BlockForm toBlock={invalidate} />}

      <Card title="Contatos bloqueados" descricao="Do mais recente para o mais antigo.">
        <div className="flex flex-col gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="size-4 rounded border-borda-forte accent-primaria-texto"
            />
            Mostrar desbloqueados
          </label>

          {contactBlockQuery.isError && (
            <Alert tone="erro" title="Não foi possível carregar a lista.">
              {contactBlockQuery.error.message}
            </Alert>
          )}

          {!contactBlockQuery.isError && (
            <Table
              label="Contatos bloqueados"
              items={contactBlockQuery.data ?? []}
              loading={contactBlockQuery.isLoading}
              rowKey={(b) => String(b.id)}
              empty="Ninguém pediu para não ser contatado ainda. Quem pedir aparece aqui, e para de receber e-mail na hora."
              columns={[
                {
                  title: 'Contato',
                  alignment: 'inicio' as const,
                  minWidth: '12rem',
                  cell: (b) => (
                    <span
                      className={
                        b.archivedAt
                          ? 'font-mono text-sm text-texto-suave line-through'
                          : 'font-mono text-sm'
                      }
                    >
                      {b.contact}
                    </span>
                  ),
                },
                {
                  title: 'Tipo',
                  cell: (b) => (b.tipo === 'email' ? 'E-mail' : 'Telefone'),
                },
                {
                  /*
                   * A coluna existe só quando os arquivados estão à mostra.
                   * Sem ela, ativo e desbloqueado apareciam iguais na mesma
                   * tabela — e a lista dizia que alguém estava bloqueado
                   * quando não estava.
                   */
                  title: 'Situação',
                  cell: (b) =>
                    b.archivedAt ? (
                      <Badge tone="neutro">Desbloqueado</Badge>
                    ) : (
                      <Badge tone="erro">Bloqueado</Badge>
                    ),
                },
                {
                  title: 'Origem',
                  minWidth: '9rem',
                  cell: (b) => (
                    <Badge tone={b.origin === 'pedido_do_titular' ? 'alerta' : 'neutro'}>
                      {LABEL_ORIGIN[b.origin]}
                    </Badge>
                  ),
                },
                { title: 'Motivo', cell: (b) => b.reason ?? '—' },
                { title: 'Desde', cell: (b) => formatInstant(b.since) },
                {
                  title: '',
                  hiddenLabel: 'Ação',
                  alignment: 'fim',
                  // já arquivado não tem o que desbloquear: o botão daria 404
                  cell: (b) =>
                    canEdit && !b.archivedAt ? (
                      <ButtonUnblock block={b} toUnblock={invalidate} />
                    ) : null,
                },
              ]}
            />
          )}
        </div>
      </Card>
    </Page>
  );
}

/** Bloquear à mão: quem ligou pedindo, e o e-mail que a operação sabe que quica. */
function BlockForm({ toBlock }: { toBlock: () => void }) {
  const [tipo, setType] = useState<ContactBlock['tipo']>('telefone');
  const [contact, setContact] = useState('');
  const [origin, setOrigin] = useState<ContactBlock['origin']>('pedido_do_titular');
  const [reason, setReason] = useState('');
  /*
   * Dois erros, e não um.
   *
   * A versão anterior mandava toda falha para o `erro` do campo de contato — e
   * o `Field` marca `aria-invalid` e pinta a borda naquele input. Um 500 ou uma
   * queda de rede acusavam o e-mail que a pessoa digitou certo, e ela ficava
   * corrigindo o que não estava errado. O servidor diz de quem é a culpa em
   * `detalhes.campo`; jogar essa informação fora era o defeito.
   */
  const [contactError, contactsetError] = useState<string | null>(null);
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);

  const block = useMutation({
    mutationFn: async () => {
      const r = await api.platform.blockContact({
        body: {
          tipo,
          contact,
          origin,
          reason: reason.trim() === '' ? null : reason.trim(),
        },
      });
      if (r.status !== 201) {
        const body = r.body as { details?: { field?: string } } | undefined;
        const error = new Error(errorMessage(r.body, 'Não foi possível bloquear.'));
        error.name = body?.details?.field === 'contato' ? 'campo' : 'geral';
        throw error;
      }
      return r.body;
    },
    onSuccess: () => {
      setContact('');
      setReason('');
      contactsetError(null);
      setErrorGeneral(null);
      toBlock();
    },
    onError: (e: Error) => {
      if (e.name === 'campo') contactsetError(e.message);
      else setErrorGeneral(e.message);
    },
  });

  return (
    <Card title="Bloquear um contato" descricao="Vale imediatamente, em todas as bases.">
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          // a tentativa nova começa sem o erro da anterior
          contactsetError(null);
          setErrorGeneral(null);
          block.mutate();
        }}
      >
        {errorGeneral && <Alert tone="erro">{errorGeneral}</Alert>}

        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Tipo"
            value={tipo}
            onChange={(e) => setType(e.target.value as ContactBlock['tipo'])}
          >
            <option value="telefone">Telefone</option>
            <option value="email">E-mail</option>
          </Select>

          <Field
            label={tipo === 'email' ? 'E-mail' : 'Telefone com DDD'}
            required
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder={tipo === 'email' ? 'pessoa@exemplo.com.br' : '85 99988-7766'}
            error={contactError}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Origem"
            value={origin}
            /*
             * A origem é escolhida por quem registra, e não deduzida: `pedido do
             * titular` é direito da pessoa e não se desfaz por conveniência;
             * `retorno inválido` é dado operacional e some quando o contato é
             * corrigido. Sem a distinção, a lista fica impossível de limpar sem
             * risco de desfazer um pedido.
             */
            help="Pedido da pessoa não se desfaz por conveniência."
            onChange={(e) => setOrigin(e.target.value as ContactBlock['origin'])}
          >
            <option value="pedido_do_titular">Pedido da pessoa</option>
            <option value="retorno_invalido">Retorno inválido</option>
            <option value="operacional">Operacional</option>
          </Select>

          <Field
            label="Motivo"
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            help="Opcional. Quem ler daqui a um ano precisa entender."
          />
        </div>

        <div>
          <Button
            type="submit"
            loading={block.isPending}
            disabled={contact.trim() === ''}
          >
            Bloquear
          </Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * O erro fica AO LADO do botão, nunca no lugar dele.
 *
 * Trocar o controle pela mensagem tira da pessoa a única coisa que ela pode
 * fazer a respeito, e obriga a recarregar a página para recuperá-la.
 */
function ButtonUnblock({
  block,
  toUnblock,
}: {
  block: ContactBlock;
  toUnblock: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unblock = useMutation({
    mutationFn: async () => {
      const r = await api.platform.unblockContact({
        params: { id: block.id },
        body: {},
      });
      if (r.status !== 200) {
        throw new Error(errorMessage(r.body, 'Não foi possível desbloquear.'));
      }
    },
    onSuccess: () => {
      setError(null);
      setConfirming(false);
      toUnblock();
    },
    onError: (e: Error) => setError(e.message),
  });

  const controls = confirming ? (
    <span className="flex items-center gap-1.5">
      <Button
        variant="perigo"
        size="sm"
        loading={unblock.isPending}
        onClick={() => {
          setError(null);
          unblock.mutate();
        }}
      >
        Voltar a contatar
      </Button>
      <Button
        variant="discreto"
        size="sm"
        onClick={() => {
          // desistir limpa o erro junto: senão a mensagem da tentativa anterior
          // fica pendurada ao lado de um botão que não tentou nada
          setError(null);
          setConfirming(false);
        }}
      >
        Cancelar
      </Button>
    </span>
  ) : (
    <Button variant="discreto" size="sm" onClick={() => setConfirming(true)}>
      Desbloquear
    </Button>
  );

  if (!error) return controls;

  return (
    <span className="inline-flex flex-col items-end gap-1.5">
      <span role="alert" className="text-xs font-medium text-erro">
        {error}
      </span>
      {controls}
    </span>
  );
}
